/**
 * @file Nesta AI Chatbot Logic
 * @description Implements a Custom Element `<nesta-chat>` for the floating AI chatbot widget.
 * Handles the UI interactions (toggling, sending messages), state management (localStorage session),
 * and API communication with the backend.
 * 
 * @version 1.0.0
 */

/**
 * Custom HTML element representing the Nesta AI Chat widget.
 * @class NestaChat
 * @extends {HTMLElement}
 */
class NestaChat extends HTMLElement {
  /**
   * Initializes the Custom Element, sets up default state properties, 
   * and caches DOM element selectors for later use.
   */
  constructor() {
    super();
    
    /** @type {string} LocalStorage key used to persist the user's chat session. */
    this.sessionKey = 'nesta_session';
    
    /** @type {string} SessionStorage key for open state */
    this.sessionOpenKey = 'nesta_is_open';
    
    /** @type {string} SessionStorage key for message history */
    this.sessionHistoryKey = 'nesta_history';

    /** @type {string} Base API URL read from Liquid schema */
    this.baseUrl = this.getAttribute('data-api-url') || '';
    
    /** @type {boolean} Indicates whether the chat window is currently visible. */
    this.isOpen = sessionStorage.getItem(this.sessionOpenKey) === 'true';
    
    /** @type {boolean} Indicates if the initial API /start request has been made. */
    this.isInitialized = false;
    
    /** @type {boolean} Indicates if an API request is currently in progress. */
    this.isLoading = false;

    // --- DOM Selectors ---
    
    /** @type {HTMLElement|null} The floating action button element. */
    this.fab = this.querySelector('.nesta-chat__fab');
    
    /** @type {HTMLElement|null} The default chat icon inside the FAB. */
    this.fabIconOpen = this.querySelector('.nesta-chat__fab-icon');
    
    /** @type {HTMLElement|null} The close (X) icon inside the FAB. */
    this.fabIconClose = this.querySelector('.nesta-chat__fab-icon-close');
    
    /** @type {HTMLElement|null} The main chat window container. */
    this.window = this.querySelector('.nesta-chat__window');
    
    /** @type {HTMLButtonElement|null} The close button inside the chat header. */
    this.closeBtn = this.querySelector('.nesta-chat__close-btn');
    
    /** @type {HTMLElement|null} The container where chat bubbles are rendered. */
    this.messagesContainer = this.querySelector('.nesta-chat__messages');
    
    /** @type {HTMLElement|null} The scrollable body of the chat window. */
    this.bodyContainer = this.querySelector('.nesta-chat__body');
    
    /** @type {HTMLInputElement|null} The text input field for user messages. */
    this.input = this.querySelector('.nesta-chat__input');
    
    /** @type {HTMLButtonElement|null} The button used to submit a user message. */
    this.sendBtn = this.querySelector('.nesta-chat__send-btn');
  }

  /**
   * Lifecycle callback invoked when the element is appended to the document.
   * Responsible for attaching all necessary event listeners.
   */
  connectedCallback() {
    this.#setupListeners();
    
    // Listen for custom toggle event from external buttons
    document.addEventListener('nesta-chat:toggle', (e) => {
      this.#toggleChat(e.detail?.forceState);
    });
    
    // Restore window state if it was open on the previous page
    if (this.isOpen) {
      this.#toggleChat(true);
    }
  }

  /**
   * Attaches click and keyboard event listeners to the interactive elements.
   */
  #setupListeners() {
    if (this.fab) {
      this.fab.addEventListener('click', () => this.#toggleChat());
    }
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.#toggleChat());
    }

    if (this.sendBtn) {
      this.sendBtn.addEventListener('click', () => this.#handleSend());
    }

    if (this.input) {
      // Allow sending messages by pressing the "Enter" key without Shift
      this.input.addEventListener('keypress', (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          this.#handleSend();
        }
      });

      // Disable the send button if the input is empty and auto-resize textarea
      this.input.addEventListener('input', () => {
        this.sendBtn.disabled = this.input.value.trim() === '' || this.isLoading;
        
        // Auto-resize
        this.input.style.height = 'auto';
        this.input.style.height = this.input.scrollHeight + 'px';
      });
    }

    // Accessibility: Trap focus inside chat window when open and allow Escape to close
    this.window.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.#toggleChat(false);
        e.preventDefault();
        return;
      }

      if (e.key === 'Tab') {
        const focusableElements = this.window.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (focusableElements.length === 0) return;
        
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            lastElement.focus();
            e.preventDefault();
          }
        } else {
          if (document.activeElement === lastElement) {
            firstElement.focus();
            e.preventDefault();
          }
        }
      }
    });
  }

  /**
   * Toggles the visibility of the chat window and handles initial session loading.
   * @param {boolean|null} forceState - Optional flag to force open or close
   * @async
   */
  async #toggleChat(forceState = null) {
    if (forceState !== null) {
      this.isOpen = forceState;
    } else {
      this.isOpen = !this.isOpen;
    }
    
    sessionStorage.setItem(this.sessionOpenKey, this.isOpen);

    if (this.isOpen) {
      // Open the chat window
      this.window.classList.add('is-open');
      this.window.setAttribute('aria-hidden', 'false');
      if (this.fab) this.fab.setAttribute('aria-expanded', 'true');
      this.fabIconOpen.style.display = 'none';
      this.fabIconClose.style.display = 'block';

      // Auto focus input after the CSS transition finishes
      this.window.addEventListener('transitionend', (e) => {
        if (e.propertyName === 'opacity' || e.propertyName === 'transform') {
          this.input.focus();
        }
      }, { once: true });

      // If opening for the very first time, initialize the session
      if (!this.isInitialized) {
        await this.#initializeSession();
      }
    } else {
      // Close the chat window
      this.window.classList.remove('is-open');
      this.window.setAttribute('aria-hidden', 'true');
      if (this.fab) this.fab.setAttribute('aria-expanded', 'false');
      this.fabIconOpen.style.display = 'block';
      this.fabIconClose.style.display = 'none';
    }

    // Dispatch state change event
    document.dispatchEvent(new CustomEvent('nesta-chat:state-changed', {
      detail: { isOpen: this.isOpen }
    }));
  }

  /**
   * Initializes the chat session. Checks for an existing session in localStorage.
   * If none exists, it calls the `/start` API to get the initial greeting and UUID.
   * @async
   */
  async #initializeSession() {
    let existingSession = localStorage.getItem(this.sessionKey);

    // Gadget API expects numeric GadgetID. If we have a legacy UUID, clear it.
    if (existingSession && !/^\d+$/.test(existingSession)) {
      existingSession = null;
      localStorage.removeItem(this.sessionKey);
      sessionStorage.removeItem(this.sessionHistoryKey);
      
      // Also clear any lingering messages in the DOM if we are resetting
      if (this.messagesContainer) {
        this.messagesContainer.innerHTML = '';
      }
    }

    if (!existingSession) {
      // No session found, start a new one
      this.isInitialized = true;
      await this.#fetchApi('/start', null, true);
    } else {
      // Session exists, flag as initialized
      this.isInitialized = true;

      // Hydrate history from sessionStorage if available
      const history = JSON.parse(sessionStorage.getItem(this.sessionHistoryKey) || '[]');
      
      if (history.length > 0) {
        history.forEach(msg => {
          this.#appendMessage(msg.text, msg.sender, msg.products, msg.suggestions, false);
        });
      } else if (this.messagesContainer.children.length === 0) {
        // Output a greeting if the chat is completely empty (no chat history loaded)
        const welcomeMsg = this.getAttribute('data-welcome-msg') || "Welcome back! How can I help you today?";
        this.#appendMessage(welcomeMsg, 'bot');
      }
    }
  }

  /**
   * Processes the user's input, renders it to the UI, and sends it to the API.
   * @async
   * @param {string|null} messageText - Optional forced text (used for suggestion pills).
   */
  async #handleSend(messageText = null) {
    const text = messageText || this.input.value.trim();
    if (!text || this.isLoading) return;

    // Clear input field and disable send button
    this.input.value = '';
    this.input.style.height = 'auto'; // Reset textarea height
    this.sendBtn.disabled = true;

    // Remove any existing suggestion pills from the UI before sending
    this.#removeSuggestions();

    // Render the user's message in the chat window
    this.#appendMessage(text, 'user');

    const sessionId = localStorage.getItem(this.sessionKey);

    // Send the message to the backend
    await this.#fetchApi('/chat', {
      session_id: sessionId,
      message: text
    });
  }

  /**
   * Universal wrapper for communicating with the backend API.
   * Handles loading states, error catching, and UI updates based on the JSON response.
   * 
   * @async
   * @param {string} endpoint - The API path to call (e.g., '/start' or '/chat').
   * @param {Object|null} bodyData - The JSON payload to send (null for GET/empty POST).
   * @param {boolean} isStart - Flag indicating if this is the initial session creation.
   * @throws Will throw an error if the HTTP response is not ok (e.g., 400 or 500).
   */
  async #fetchApi(endpoint, bodyData = null, isStart = false) {
    this.isLoading = true;
    this.#showTypingIndicator();

    try {
      const options = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      };

      if (bodyData) {
        options.body = JSON.stringify(bodyData);
      }

      const response = await fetch(`${this.baseUrl}${endpoint}`, options);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Remove typing dots before appending actual message
      this.#hideTypingIndicator();

      // Save the session ID to localStorage if provided by the API
      if (data.session_id) {
        localStorage.setItem(this.sessionKey, data.session_id);
      }

      // Render the bot's reply, including any products or suggestions
      if (data.reply) {
        this.#appendMessage(data.reply, 'bot', data.products, data.suggestions);
      }

    } catch (error) {
      console.error('Nesta Chat API Error:', error);
      this.#hideTypingIndicator();
      const errorMsg = this.getAttribute('data-error-msg') || "Sorry, I'm having trouble connecting right now. Please try again.";
      this.#appendMessage(errorMsg, 'bot', null, null, false);
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Creates a message bubble and appends it to the chat container.
   * Also parses simple Markdown (bold text, newlines) and URLs.
   * 
   * @param {string} text - The raw message text to display.
   * @param {'user'|'bot'} sender - Determines the styling (alignment, background color).
   * @param {Array<Object>|null} products - Optional array of product objects to render.
   * @param {Array<string>|null} suggestions - Optional array of quick-reply suggestion strings.
   * @param {boolean} save - Whether to save this message to sessionStorage
   */
  #appendMessage(text, sender, products = null, suggestions = null, save = true) {
    if (save) {
      this.#saveHistory(text, sender, products, suggestions);
    }
    
    const msgDiv = document.createElement('div');
    msgDiv.classList.add('nesta-chat__message', `nesta-chat__message--${sender}`);

    // Escape raw text to prevent XSS
    let formattedText = this.#escapeHTML(text);

    // Parse markdown (**bold**, headings, lists)
    formattedText = formattedText
      .replace(/^### (.*$)/gim, '<h3>$1</h3>')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/^-\s+(.*$)/gim, '<li>$1</li>')
      .replace(/^(\d+\.)\s+(.*$)/gim, '<li>$2</li>')
      .replace(/\n/g, '<br>');

    // Clean up list wrappers if any <li> were created
    if (formattedText.includes('<li>')) {
      formattedText = formattedText.replace(/(<li>.*<\/li>)/s, '<ul class="nesta-chat__list">$1</ul>');
    }

    // Parse simple URLs into beautiful inline buttons
    const viewProductText = this.getAttribute('data-view-product') || "View Product";
    formattedText = formattedText.replace(/(https?:\/\/[^\s<]+[^\s<.,!?])/g, `<a href="$1" target="_blank" class="nesta-chat__inline-btn"><span>${viewProductText}</span><svg viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z" clip-rule="evenodd"/></svg></a>`);

    msgDiv.innerHTML = formattedText;
    this.messagesContainer.appendChild(msgDiv);

    // If API returned products, append the horizontal carousel
    if (products && products.length > 0) {
      const productsHtml = this.#createProductsCarousel(products);
      this.messagesContainer.appendChild(productsHtml);
    }

    // If API returned suggestions, append the interactive pills
    if (suggestions && suggestions.length > 0) {
      const suggestionsHtml = this.#createSuggestions(suggestions);
      this.messagesContainer.appendChild(suggestionsHtml);
    }

    this.#scrollToBottom();
  }

  /**
   * Saves a message to sessionStorage to persist history across page navigations.
   * @param {string} text 
   * @param {string} sender 
   * @param {Array<Object>|null} products 
   * @param {Array<string>|null} suggestions 
   */
  #saveHistory(text, sender, products, suggestions) {
    try {
      const history = JSON.parse(sessionStorage.getItem(this.sessionHistoryKey) || '[]');
      history.push({ text, sender, products, suggestions });
      if (history.length > 50) history.shift();
      sessionStorage.setItem(this.sessionHistoryKey, JSON.stringify(history));
    } catch (e) {
      console.warn('Failed to save chat history', e);
    }
  }

  /**
   * Generates a horizontal scrolling container of mini product cards.
   * 
   * @param {Array<Object>} products - The products returned by the API.
   * @returns {HTMLElement} The constructed DOM element containing the carousel.
   */
  #createProductsCarousel(products) {
    const container = document.createElement('div');
    container.classList.add('nesta-chat__products');
    
    const addToCartText = this.getAttribute('data-add-to-cart') || "Add to cart";
    const addedText = this.getAttribute('data-added') || "Added!";
    const errorText = this.getAttribute('data-error-btn') || "Error";

    products.forEach(p => {
      const card = document.createElement('div');
      card.classList.add('nesta-chat__product-card');
      
      const pUrl = p.url || p.link || (p.handle ? `/products/${p.handle}` : '');

      card.innerHTML = `
        <a href="${pUrl}" target="_blank" style="display:block;"><img src="${p.image}" alt="${p.title}" class="nesta-chat__product-image" loading="lazy"></a>
        <div class="nesta-chat__product-info">
          <h4 class="nesta-chat__product-title" title="${p.title}">${p.title}</h4>
          <span class="nesta-chat__product-price">${p.price}</span>
          <button class="nesta-chat__product-btn nesta-chat__add-to-cart" style="background: var(--nesta-send-btn, #ff5a79); color: #fff; cursor: pointer; border: none;">
            ${addToCartText}
          </button>
        </div>
      `;
      
      const btn = card.querySelector('.nesta-chat__add-to-cart');
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        
        if (!pUrl) {
          console.error("Product URL/handle is missing from API response:", p);
          btn.innerHTML = errorText;
          setTimeout(() => { btn.innerHTML = addToCartText; }, 2000);
          return;
        }

        const originalText = btn.innerHTML;
        btn.innerHTML = '...';
        btn.disabled = true;
        
        try {
          const res = await fetch(pUrl + '.js');
          const data = await res.json();
          const variantId = data.variants[0].id;
          
          await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: variantId, quantity: 1 })
          });
          
          if (window.updateMiniCart) {
            window.updateMiniCart(true);
          }
          btn.innerHTML = addedText;
          setTimeout(() => { btn.innerHTML = originalText; btn.disabled = false; }, 2000);
        } catch (err) {
          console.error(err);
          btn.innerHTML = errorText;
          setTimeout(() => { btn.innerHTML = originalText; btn.disabled = false; }, 2000);
        }
      });

      container.appendChild(card);
    });

    return container;
  }

  /**
   * Escapes HTML entities to prevent Cross-Site Scripting (XSS).
   * @param {string} str - Raw input string
   * @returns {string} - Sanitized string safe for innerHTML
   */
  #escapeHTML(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  /**
   * Generates clickable "quick reply" pill buttons below a bot message.
   * 
   * @param {Array<string>} suggestions - The string suggestions returned by the API.
   * @returns {HTMLElement} The constructed DOM element containing the buttons.
   */
  #createSuggestions(suggestions) {
    const container = document.createElement('div');
    container.classList.add('nesta-chat__suggestions');

    suggestions.forEach(s => {
      const btn = document.createElement('button');
      btn.classList.add('nesta-chat__suggestion-pill');
      btn.innerText = s;
      
      // Clicking a suggestion sends its text immediately as a user message
      btn.addEventListener('click', () => {
        this.#handleSend(s);
      });
      container.appendChild(btn);
    });

    return container;
  }

  /**
   * Cleans up the DOM by removing any existing suggestion pills.
   * Called before sending a new message to prevent users from clicking old suggestions.
   */
  #removeSuggestions() {
    const existingSuggestions = this.querySelectorAll('.nesta-chat__suggestions');
    existingSuggestions.forEach(el => el.remove());
  }

  /**
   * Displays an animated "3 bouncing dots" typing indicator to signal the bot is "thinking".
   */
  #showTypingIndicator() {
    // Don't add multiple indicators if one already exists
    if (this.querySelector('.nesta-chat__typing')) return;

    const indicator = document.createElement('div');
    indicator.classList.add('nesta-chat__typing');
    indicator.innerHTML = `
      <div class="nesta-chat__typing-dot"></div>
      <div class="nesta-chat__typing-dot"></div>
      <div class="nesta-chat__typing-dot"></div>
    `;
    this.messagesContainer.appendChild(indicator);
    this.#scrollToBottom();
  }

  /**
   * Removes the typing indicator from the DOM once the API response is received.
   */
  #hideTypingIndicator() {
    const indicator = this.querySelector('.nesta-chat__typing');
    if (indicator) indicator.remove();
  }

  /**
   * Forces the chat window body to scroll down to the most recent message.
   */
  #scrollToBottom() {
    this.bodyContainer.scrollTo({
      top: this.bodyContainer.scrollHeight,
      behavior: 'smooth'
    });
  }
}

// Defer registration to improve page load performance
function initNestaChat() {
  if (!customElements.get('nesta-chat')) {
    customElements.define('nesta-chat', NestaChat);
  }
}

if ('requestIdleCallback' in window) {
  requestIdleCallback(initNestaChat, { timeout: 2000 });
} else {
  setTimeout(initNestaChat, 500);
}
